import React, { useContext, useEffect, useState } from 'react'
import * as L from './layout.styles';
import DocumentTitle from "react-document-title";
import { ThemeContext } from "@fluentui/react-theme-provider";
import Auth from "../../Auth/Auth";
import { RouteComponentProps, useHistory, useLocation } from "react-router-dom";
import { Spinner, SpinnerSize, Toggle, TextField, DefaultButton } from "@fluentui/react";
import PortfolioModelWalletService from '@/services/PortfolioModelWalletService';
import SavingsPlanService, { SavingsPlanType } from '@/services/SavingsPlanService';
import { CartEP } from './components/CartEP';
import { GetCartInfoEP } from './components/CartTableEP';
import { roundToDecimalPlaces } from '@/utils/numberFormatter';
import { cartStateEP, clearCartEP, updateModelPortfolioIdEP, updateModelPortfolioNameEP } from '@/store/educationalcart/actions';
import { connect, useDispatch } from "react-redux";
import { setActiveSavingsPlan } from '@/store/auth/actions';
import { ErrorModal } from '@/components/Modals/ErrorModal';
import { createurl } from '@/utils/createUrl';
import CustomIcon from "../../components/icons/CustomIcon";
import { ModalChangePlanNameWithoutButton } from '@/components/SavePlans/ModalChangePlanNameWithoutButton';

export enum CartModesEP
{
    VIEW_MODEL_PORTFOLIO = "VIEW_MODEL_PORTFOLIO",
    CREATE_MODEL_PORTFOLIO = "CREATE_MODEL_PORTFOLIO",
    EDIT_MODEL_PORTFOLIO = "EDIT_MODEL_PORTFOLIO"
}

interface CartScreenEPProps extends RouteComponentProps
{
    cartLines: any[];
    savingPlanType?: number;
    portfolioModelId?: number;
    current_state: CartModesEP;
    modelPortfolioName: string;
    modelPortfolioId: string;
    competitionId?: number;
    originCompetition?: string;
    isCompetitionPromotionated?: boolean;
    competitionName?: string;
}

export function mapStateToProps(state: any)
{
    // Sin competición los datos deben quedar undefined (no false): tras guardar se comprueba
    // competitionId !== undefined para decidir si se vuelve a la competición o a /savingsPlans
    return{
        cartLines: state.educationalCart.cartLines,
        current_state: state.educationalCart.current_state,
        modelPortfolioId: state.educationalCart.modelPortfolioId,
        modelPortfolioName: state.educationalCart.modelPortfolioName,
        competitionId: state.educationalCart.competitionInfo?.competitionId,
        originCompetition: state.educationalCart.competitionInfo?.origin,
        isCompetitionPromotionated: state.educationalCart.competitionInfo?.isPromotionated,
        competitionName: state.educationalCart.competitionInfo?.name,
    };
}

const TMPCartEducationalPlanScreen = (props: CartScreenEPProps) =>
{
    const { current_state, modelPortfolioId, modelPortfolioName, competitionId, originCompetition, isCompetitionPromotionated, competitionName } = props;

    const dispatch = useDispatch();
    const theme = useContext(ThemeContext);
    const [plan, setPlan] = useState(Auth.getActivePlan());
    let user = Auth.getUserProfile();
    const location = useLocation();
    let state: any = location.state;
    const history = useHistory();
    const [planName, setPlanName] = useState(plan?.name ?? "");
    const [loadingSavingsPlan, setLoadingSavingsPlan] = useState(false);
    const [modelWalletName, setModelWalletName] = useState<string>("");
    const [loadingModelWallet, setLoadingModelWallet] = useState<boolean>(false);
    const [error, setError] = useState({ status: false, message: "" });
    const [modalToEdit, setModalToEdit] = useState(false);
    const [cartModeStatus, setCartModeStatus] = useState<CartModesEP>(current_state);
    const [editing, setEditing] = useState<boolean>(false);
    // La cartera modelo solo puede crearse en un plan de Educación Financiera (tipo 5) con id
    const hasEducationalPlan = plan?.type === 5 && plan?.id !== undefined && plan?.id !== null;
    // Al editar una cartera existente no se usa el plan (updateModelWallet va por el id de la cartera)
    const isEditingExistingPortfolio = modelPortfolioId !== undefined && modelPortfolioId !== "";
    const [resolvingPlan, setResolvingPlan] = useState<boolean>(!hasEducationalPlan && !isEditingExistingPortfolio);

    useEffect(() =>
    {
        if(props.current_state !== cartModeStatus)
        {
            setCartModeStatus(current_state);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.current_state]);

    useEffect(() =>
    {
        if(modelPortfolioName)
        {
            setEditing(true);
            setModelWalletName(modelPortfolioName)
        }
    }, [modelPortfolioName]);

    useEffect(() =>
    {
        if(props.cartLines.length > 0 && props.cartLines[0].savingsPlanId !== null && props.cartLines[0].savingsPlanId !== undefined)
        {
            setLoadingSavingsPlan(true);
            SavingsPlanService.getSavingsPlan(props.cartLines[0].savingsPlanId).then((savingsPlan: SavingsPlanType) =>
            {
                if(savingsPlan !== undefined)
                {
                    setPlanName(savingsPlan.name);
                    setLoadingSavingsPlan(false);
                }
            })
            .catch((error: any) =>
            {
                // Se normaliza el error (el objeto de axios no tiene la forma {status, message} del modal) y se quita el spinner
                setError({ status: true, message: (typeof error?.response?.data === "string" && error.response.data) || "No se ha podido cargar la información del plan. Por favor, inténtalo de nuevo más tarde." });
                setLoadingSavingsPlan(false);
            });
        }
    }, [props.cartLines]);

    useEffect(() =>
    {
        const fetchSavingsPlans = async () =>
        {
            try
            {
                const res = await SavingsPlanService.getSavingsPlansAndStatusLite(user.id);
                const educationalPlans = res.filter((e: any) => e.type === 5);

                if(educationalPlans.length > 0)
                {
                    // Buscar primero un plan activo (status = 4) entre los educacionales
                    const activePlan = educationalPlans.find((e: any) => e.status === 4);
                    
                    if(activePlan)
                    {
                        Auth.setActivePlan(activePlan);
                        dispatch(setActiveSavingsPlan(activePlan));
                    }
                    else
                    {
                        // Si no hay activo, tomar el primer plan educacional
                        Auth.setActivePlan(educationalPlans[0]);
                        dispatch(setActiveSavingsPlan(educationalPlans[0]));
                    }

                    const resolvedPlan = Auth.getActivePlan();
                    setPlan(resolvedPlan);
                    setPlanName(resolvedPlan?.name ?? "");
                }
                else
                {
                    setError({status: true, message: "Se ha producido un error al cargar los planes de cartera"});
                }
            }
            catch(error)
            {
                setError({status: true, message: "Se ha producido un error al cargar los planes de cartera"});
            }
            finally
            {
                setResolvingPlan(false);
            }
        };

        // Antes se comprobaba planName === undefined, que nunca se cumplía (se inicializa a ""), y la
        // cartera se guardaba en el plan activo fuese cual fuese (gestionado, o vacío si no había activo).
        // Solo se resuelve al crear: al editar no se usa el plan y cambiar el activo vaciaría el carrito.
        if(!hasEducationalPlan && !isEditingExistingPortfolio)
        {
            fetchSavingsPlans();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const modelWalletNameChangeHandler = (newValue: string | undefined) =>
    {
        if(newValue !== undefined)
        {
            setModelWalletName(newValue);
        }
    };

    // Igual que en /cart y en el backend: no se guarda una cartera modelo sin fondos ni con un total distinto del 100%
    // (antes bastaba con el nombre y se guardaban carteras vacías)
    const { modelPercentageTotal } = GetCartInfoEP(props.cartLines);
    const isModelCompositionValid = props.cartLines.length > 0 && roundToDecimalPlaces(modelPercentageTotal, 2) === 100;

    const saveModelWalletOnClickHandler = () =>
    {
        setLoadingModelWallet(true);
        let portfolioModelId: number | null = null;

        if(modelPortfolioId !== undefined && modelPortfolioId !== "")
        {
            PortfolioModelWalletService.updateModelWallet(modelWalletName, parseInt(modelPortfolioId), props.cartLines).then((res) =>
            {
                portfolioModelId = res.id;

                dispatch(clearCartEP());
                dispatch(cartStateEP(CartModesEP.CREATE_MODEL_PORTFOLIO));
                dispatch(updateModelPortfolioNameEP(""));
                dispatch(updateModelPortfolioIdEP(""));
                setLoadingModelWallet(false);

                if(competitionId !== undefined)
                {
                    if(originCompetition === "participate")
                    {
                        // El suscrito viene de CompetitionParticipate; el no suscrito, de CompetitionParticipateNoActivePlan
                        const participatePath = Auth.isSubscribed() ? `/competitions/join/${competitionId}` : `/competitions/join/nsv/${competitionId}`;
                        history.push(participatePath, { portfolioModelId: portfolioModelId });
                    }

                    if(originCompetition === "edit_model_portfolio")
                    {
                        if(isCompetitionPromotionated)
                        {
                            history.push(`/competition/${createurl(competitionName)}-${competitionId}`, { competitionId: competitionId });
                        }
                        else
                        {
                            history.push(`/competitions/${competitionId}`);
                        }
                    }
                }
                else
                {
                    history.push("/savingsPlans");
                }
            })
            .catch((error) =>
            {
                console.error("Error al actualizar el modelo de cartera:", error);
                setError({ status: true, message: (typeof error?.response?.data === "string" && error.response.data) || "No se ha podido guardar la cartera modelo. Por favor, inténtalo de nuevo más tarde." });
                setLoadingModelWallet(false);
            });
        }
        else
        {
            // Nunca se crea la cartera sin un plan de Educación Financiera resuelto (antes podía ir con id undefined)
            if(!hasEducationalPlan)
            {
                setError({status: true, message: "Se ha producido un error al cargar los planes de cartera"});
                setLoadingModelWallet(false);
                return;
            }

            PortfolioModelWalletService.saveModelWallet(plan.id, modelWalletName, props.cartLines).then((res) =>
            {
                portfolioModelId = res.id;
                dispatch(clearCartEP());
                dispatch(cartStateEP(CartModesEP.CREATE_MODEL_PORTFOLIO));
                dispatch(updateModelPortfolioIdEP(""));
                dispatch(updateModelPortfolioNameEP(""));
                setLoadingModelWallet(false);

                if(competitionId !== undefined)
                {
                    if(originCompetition === "participate")
                    {
                        // El suscrito viene de CompetitionParticipate; el no suscrito, de CompetitionParticipateNoActivePlan
                        const participatePath = Auth.isSubscribed() ? `/competitions/join/${competitionId}` : `/competitions/join/nsv/${competitionId}`;
                        history.push(participatePath, { portfolioModelId: portfolioModelId });
                    }
                    if(originCompetition === "edit_model_portfolio")
                    {
                        if(isCompetitionPromotionated)
                        {
                            history.push(`/competition/${createurl(competitionName)}-${competitionId}`, { competitionId: competitionId });
                        }
                        else
                        {
                            history.push(`/competitions/${competitionId}`);
                        }
                    }
                }
                else
                {
                    history.push("/savingsPlans");
                }
            }).catch((error) =>
            {
                console.error("Error al crear la cartera modelo", error);
                setError({ status: true, message: (typeof error?.response?.data === "string" && error.response.data) || "No se ha podido guardar la cartera modelo. Por favor, inténtalo de nuevo más tarde." });
                setLoadingModelWallet(false);
            });
        }
    };

    return(
        <DocumentTitle title="IronIA - Carrito">
            <L.Main theme={theme}>
                <L.HeaderContainer>
                    <L.TitleContainer>
                        Carrito de Inversiones
                    </L.TitleContainer>
                    <ErrorModal title="Ha ocurrido un error al procesar tu solicitud"
                        body=
                        {
                            <div>
                                <p>{error.message}</p>
                            </div>
                        }
                        btnSubmitText="Aceptar"
                        showModal={error.status}
                        onSubmit={() =>
                        {
                            setError({status: false, message: ""});
                        }}
                        onClose={() =>
                        {
                            setError({status: false, message: ""});
                        }}
                    />
                    {plan !== undefined &&
                    (
                        <React.Fragment>
                            <L.PlanTitleContainer>
                                {(loadingSavingsPlan || resolvingPlan) && <Spinner size={SpinnerSize.small} className="button-spinner" style={{ paddingTop: "4px" }} />}
                                {!loadingSavingsPlan && !resolvingPlan && <L.SubTitleContainer>{planName}</L.SubTitleContainer>}
                            </L.PlanTitleContainer>
                        </React.Fragment>
                    )}
                    <L.ModelWalletInputsContainer>
                        <L.ModelWalletToggle>
                            <Toggle label={<L.ModelWalletLabel>Cartera Modelo</L.ModelWalletLabel>}
                                inlineLabel
                                onText=" "
                                offText=" "
                                checked={true}
                                disabled={true}
                                styles={
                                {
                                    root: { margin: 0 },
                                    label: { margin: 0, paddingBottom: 0, paddingTop: "8px" },
                                    container: { paddingTop: "13px" }
                                }}
                            />
                        </L.ModelWalletToggle>
                        <div style={{display: "flex"}}>
                            <TextField className="model-wallet-textfield"
                                theme={theme}
                                value={modelWalletName}
                                onChange={(e, newValue) => modelWalletNameChangeHandler(newValue)}
                                disabled={editing}
                                styles={
                                {
                                    field:
                                    {
                                        borderRadius: "100px !important"
                                    },
                                    fieldGroup:
                                    {
                                        "&:hover":
                                        {
                                            borderRadius: "100px !important"
                                        },
                                        "&::after":
                                        {
                                            borderRadius: "100px !important"
                                        },
                                        borderRadius: "100px !important",
                                        height: "35px !important"
                                    },
                                    root:
                                    {
                                        margin: "0 1em 0 0 !important",
                                        paddingTop: "7px"
                                    }
                                }}
                            />
                            {cartModeStatus === CartModesEP.EDIT_MODEL_PORTFOLIO && <div style={{marginRight: "10px"}}> <CustomIcon iconName="Edit" size="17" color="#CC214F" paddingTop="16px" onClick={() =>
                            {
                                setModalToEdit(true);
                            }}
                            /></div>}
                            {loadingModelWallet && <Spinner size={SpinnerSize.medium} className="button-spinner" />}
                            {!loadingModelWallet &&
                            (
                                <DefaultButton className="button mini-primary-button"
                                    disabled={!modelWalletName || !isModelCompositionValid || resolvingPlan || (!isEditingExistingPortfolio && !hasEducationalPlan)}
                                    onClick={saveModelWalletOnClickHandler}
                                    styles={{root: {height: "30px", marginTop: "7px !important", marginLeft: "6px"}}}
                                >
                                    Guardar
                                </DefaultButton>
                            )}
                        </div>
                    </L.ModelWalletInputsContainer>
                </L.HeaderContainer>
                <L.SeparatorTop />
                <CartEP cartModeStatus={props.current_state} savingPlanType={state?.savingPlanType} currentInstrumentIds={[]}
                    onChangeCartModeStatus={(newStatus: CartModesEP) =>
                    {
                        dispatch(cartStateEP((newStatus)));
                    }}
                />
                <ModalChangePlanNameWithoutButton name={modelWalletName} paddingTopIcon={"9%"} isModelPortfolio={true}
                    showModal={modalToEdit} setShowModal={setModalToEdit}
                    onSubmit={(item) =>
                    {
                        setModelWalletName(item)
                    }}
                />
            </L.Main>
        </DocumentTitle>
    )
}

export const CartEducationalPlanScreen = connect(mapStateToProps)(TMPCartEducationalPlanScreen);